import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { serviceProviderApi, avvContractApi, organizationApi } from '../services/api';
import {
  ServiceProviderCreate,
  ServiceProviderUpdate,
  ServiceProvider,
  ServiceProviderRole,
  ServiceProviderTemplate,
  ServiceProviderTemplateCategory,
  AvvContractCreate
} from '@dsgvo-buddy/types';

interface ServiceProviderWizardPageProps {
  editMode?: boolean;
}

import {
  Title,
  Text,
  Button,
  Input,
  TextArea,
  Select,
  Option,
  CheckBox,
  ProgressIndicator,
  FlexBox,
  Card,
  Label,
  Icon,
  MessageStrip,
  BusyIndicator,
  RadioButton,
  DatePicker
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import HelpTooltip from '../components/HelpTooltip';
import { fieldHelp } from '../helpers/helpTexts';
import { isoToGerman, germanToIso } from '../helpers/dateFormat';

const roleConfig: Record<ServiceProviderRole, { label: string; description: string }> = {
  [ServiceProviderRole.PROCESSOR]: {
    label: 'Auftragsverarbeiter (Art. 28 DSGVO)',
    description: 'Verarbeitet Daten in Ihrem Auftrag'
  },
  [ServiceProviderRole.JOINT_CONTROLLER]: {
    label: 'Gemeinsamer Verantwortlicher (Art. 26 DSGVO)',
    description: 'Entscheidet gemeinsam mit Ihnen über Zwecke und Mittel'
  },
  [ServiceProviderRole.RECIPIENT]: {
    label: 'Normaler Empfänger',
    description: 'Erhält Daten ohne Auftragsverarbeitung'
  }
};

const hostingLocationOptions = [
  { value: 'EU/EWR', label: 'EU/EWR' },
  { value: 'Deutschland', label: 'Deutschland' },
  { value: 'USA', label: 'USA' },
  { value: 'Schweiz', label: 'Schweiz' },
  { value: 'UK', label: 'UK' },
  { value: 'Sonstig', label: 'Sonstig' }
];

const steps = [
  { title: 'Grunddaten', icon: 'business-card' },
  { title: 'Leistung', icon: 'shipping-status' },
  { title: 'AVV', icon: 'official-document' }
];

interface ProviderFormData extends Omit<ServiceProviderCreate, 'organization_id'> {
  organization_id: number | null;
}

interface AvvFormData {
  version: string;
  start_date: string;
  end_date: string;
  subcontractors: string[];
  tom_assessment: string;
  tom_adequate: boolean;
  local_path: string;
}

const requiresAvv = (role: ServiceProviderRole): boolean =>
  role === ServiceProviderRole.PROCESSOR || role === ServiceProviderRole.JOINT_CONTROLLER;

export default function ServiceProviderWizardPage({ editMode = false }: ServiceProviderWizardPageProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(editMode);
  const [error, setError] = useState<string | null>(null);
  const [existingProvider, setExistingProvider] = useState<ServiceProvider | null>(null);
  const [templates, setTemplates] = useState<ServiceProviderTemplate[]>([]);
  const [templateCategory, setTemplateCategory] = useState('');
  const [templateSearch, setTemplateSearch] = useState('');

  const [formData, setFormData] = useState<ProviderFormData>({
    organization_id: null,
    name: '',
    role: ServiceProviderRole.PROCESSOR,
    address: '',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    service_description: '',
    hosting_location: '',
    notes: ''
  });

  const [avvData, setAvvData] = useState<AvvFormData>({
    version: '1.0',
    start_date: '',
    end_date: '',
    subcontractors: [],
    tom_assessment: '',
    tom_adequate: false,
    local_path: ''
  });

  const [subcontractorInput, setSubcontractorInput] = useState('');

  // Total steps depends on mode and role
  const totalSteps = editMode || !requiresAvv(formData.role) ? 2 : 3;
  const lastStep = totalSteps - 1;

  useEffect(() => { loadData(); }, [id, editMode]);

  const loadData = async () => {
    const [orgsResponse, templatesResponse] = await Promise.all([
      organizationApi.getAll(),
      serviceProviderApi.getTemplates()
    ]);

    if (templatesResponse.success && templatesResponse.data) {
      setTemplates(templatesResponse.data);
    }

    if (orgsResponse.success && orgsResponse.data) {
      const org = orgsResponse.data[0];
      if (org) {
        setFormData(prev => ({ ...prev, organization_id: org.id }));
        if (editMode && id) {
          const providerResponse = await serviceProviderApi.getById(parseInt(id), org.id);
          if (providerResponse.success && providerResponse.data) {
            const provider = providerResponse.data;
            setExistingProvider(provider);
            setFormData({
              organization_id: provider.organization_id,
              name: provider.name,
              role: provider.role,
              address: provider.address || '',
              contact_person: provider.contact_person || '',
              contact_email: provider.contact_email || '',
              contact_phone: provider.contact_phone || '',
              service_description: provider.service_description || '',
              hosting_location: provider.hosting_location || '',
              notes: provider.notes || ''
            });
          } else {
            navigate('/service-providers');
            return;
          }
        }
      }
    }

    setInitialLoading(false);
  };

  const handleApplyTemplate = (template: ServiceProviderTemplate) => {
    setFormData(prev => ({
      ...prev,
      name: template.name,
      role: template.role,
      address: template.address,
      service_description: template.service_description,
      hosting_location: template.hosting_location,
      notes: template.notes
    }));
  };

  const updateFormData = (field: keyof ProviderFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAvvData = (field: keyof AvvFormData, value: any) => {
    setAvvData(prev => ({ ...prev, [field]: value }));
  };

  const subInputRef = useRef('');
  const addSubcontractor = () => {
    const val = subcontractorInput.trim() || subInputRef.current.trim();
    if (val && !avvData.subcontractors.includes(val)) {
      updateAvvData('subcontractors', [...avvData.subcontractors, val]);
      setSubcontractorInput('');
      subInputRef.current = '';
    }
  };

  const removeSubcontractor = (index: number) => {
    updateAvvData('subcontractors', avvData.subcontractors.filter((_, i) => i !== index));
  };

  const validateStep = (): boolean => {
    switch (currentStep) {
      case 0: return !!(formData.name && formData.role);
      case 1: return true;
      case 2: return true; // AVV is optional
      default: return false;
    }
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => Math.min(prev + 1, lastStep));
      setError(null);
    } else {
      setError('Bitte füllen Sie alle erforderlichen Felder aus.');
    }
  };

  const handleBack = () => { setCurrentStep(prev => Math.max(prev - 1, 0)); setError(null); };

  const handleSubmit = async () => {
    if (!formData.organization_id) { setError('Organisation erforderlich'); return; }
    if (!validateStep()) { setError('Bitte füllen Sie alle erforderlichen Felder aus.'); return; }

    setLoading(true);
    setError(null);

    let providerId: number | null = null;

    if (editMode && id && existingProvider) {
      const updateData: ServiceProviderUpdate = {
        name: formData.name,
        role: formData.role,
        address: formData.address || undefined,
        contact_person: formData.contact_person || undefined,
        contact_email: formData.contact_email || undefined,
        contact_phone: formData.contact_phone || undefined,
        service_description: formData.service_description || undefined,
        hosting_location: formData.hosting_location || undefined,
        notes: formData.notes || undefined,
        organization_id: formData.organization_id
      };
      const response = await serviceProviderApi.update(parseInt(id), formData.organization_id, updateData);
      if (response.success && response.data) {
        providerId = response.data.id;
      } else {
        setError(response.error || 'Fehler beim Aktualisieren');
        setLoading(false);
        return;
      }
    } else {
      const createData: ServiceProviderCreate = {
        organization_id: formData.organization_id,
        name: formData.name,
        role: formData.role,
        address: formData.address || undefined,
        contact_person: formData.contact_person || undefined,
        contact_email: formData.contact_email || undefined,
        contact_phone: formData.contact_phone || undefined,
        service_description: formData.service_description || undefined,
        hosting_location: formData.hosting_location || undefined,
        notes: formData.notes || undefined
      };
      const response = await serviceProviderApi.create(createData);
      if (response.success && response.data) {
        providerId = response.data.id;
      } else {
        setError(response.error || 'Fehler beim Erstellen');
        setLoading(false);
        return;
      }
    }

    // Create AVV if in create mode and step 3 was visible (role requires AVV)
    // Use version as minimum indicator that user intended to create an AVV
    if (!editMode && providerId && requiresAvv(formData.role) && avvData.version) {
      const startDate = avvData.start_date || new Date().toISOString().split('T')[0];
      const avvCreate: AvvContractCreate = {
        service_provider_id: providerId,
        version: avvData.version,
        start_date: startDate,
        end_date: avvData.end_date || undefined,
        subcontractors: avvData.subcontractors.length > 0 ? avvData.subcontractors : undefined,
        tom_assessment: avvData.tom_assessment || undefined,
        tom_adequate: avvData.tom_adequate,
        local_path: avvData.local_path || undefined
      };
      await avvContractApi.create(providerId, avvCreate, formData.organization_id!);
    }

    setLoading(false);
    navigate(`/service-providers/${providerId}`);
  };

  // Step 1: Grunddaten
  const renderStep1 = () => (
    <div>
      {!editMode && templates.length > 0 && (() => {
        const categoryLabels: Record<string, string> = {
          [ServiceProviderTemplateCategory.CLOUD_HOSTING]: 'Cloud & Hosting',
          [ServiceProviderTemplateCategory.HR_PAYROLL]: 'Personal & Lohnabrechnung',
          [ServiceProviderTemplateCategory.CRM_SALES]: 'CRM & Vertrieb',
          [ServiceProviderTemplateCategory.MARKETING_EMAIL]: 'Marketing & E-Mail',
          [ServiceProviderTemplateCategory.FINANCE_TAX]: 'Finanzen & Steuern',
          [ServiceProviderTemplateCategory.COMMUNICATION]: 'Kommunikation & Zusammenarbeit',
          [ServiceProviderTemplateCategory.SECURITY_IT]: 'IT-Sicherheit',
          [ServiceProviderTemplateCategory.ANALYTICS]: 'Webanalyse & Tracking',
          [ServiceProviderTemplateCategory.OFFICE_PRODUCTIVITY]: 'Office & Produktivität',
          [ServiceProviderTemplateCategory.SHIPPING_LOGISTICS]: 'Versand & Logistik',
          [ServiceProviderTemplateCategory.PAYMENT]: 'Zahlungsdienstleister',
          [ServiceProviderTemplateCategory.LEGAL_COMPLIANCE]: 'Recht & Compliance'
        };
        const categories = Object.values(ServiceProviderTemplateCategory).filter(cat =>
          templates.some(t => t.category === cat)
        );
        let filtered = templates;
        if (templateCategory) filtered = filtered.filter(t => t.category === templateCategory);
        if (templateSearch) {
          const term = templateSearch.toLowerCase();
          filtered = filtered.filter(t =>
            t.name.toLowerCase().includes(term) ||
            t.service_description.toLowerCase().includes(term)
          );
        }

        return (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            background: 'var(--sapInformativeBackground, #e8f0fe)',
            borderRadius: '0.5rem',
            border: '1px solid var(--sapInformativeColor, #0070f2)',
            borderLeftWidth: '3px'
          }}>
            <Text style={{ fontWeight: '700', fontSize: '0.875rem', display: 'block', marginBottom: '0.5rem', color: 'var(--sapInformativeColor, #0070f2)' }}>
              Vorlage verwenden (optional)
            </Text>
            <Text style={{ fontSize: '0.8125rem', display: 'block', marginBottom: '0.75rem', color: 'var(--sapTextColor)' }}>
              Suchen Sie einen Dienstleister oder filtern Sie nach Kategorie. Alle Felder werden automatisch ausgefüllt und können anschließend angepasst werden.
            </Text>
            <div style={{ marginBottom: '0.75rem' }}>
              <Label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>Suche</Label>
              <Input
                value={templateSearch}
                onInput={(e: any) => setTemplateSearch(e.target.value)}
                placeholder="Dienstleister suchen, z.B. Google, DATEV, Stripe..."
                style={{ width: '100%' }}
                icon={<Icon name="search" />}
              />
            </div>
            <FlexBox wrap="Wrap" style={{ gap: '0.75rem' }}>
              <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
                <Label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>Kategorie</Label>
                <Select
                  onChange={(e: any) => setTemplateCategory(e.detail.selectedOption.value)}
                  style={{ width: '100%' }}
                >
                  <Option value="">-- Alle Kategorien --</Option>
                  {categories.map(cat => (
                    <Option key={cat} value={cat}>
                      {categoryLabels[cat] || cat} ({templates.filter(t => t.category === cat).length})
                    </Option>
                  ))}
                </Select>
              </div>
              <div style={{ flex: '2 1 300px', minWidth: '250px' }}>
                <Label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>Vorlage</Label>
                <Select
                  onChange={(e: any) => {
                    const name = e.detail.selectedOption.value;
                    const template = templates.find(t => t.name === name);
                    if (template) handleApplyTemplate(template);
                  }}
                  style={{ width: '100%' }}
                >
                  <Option value="">{`-- ${filtered.length} Vorlagen verfügbar --`}</Option>
                  {filtered.map(t => (
                    <Option key={t.name} value={t.name}>{t.name}</Option>
                  ))}
                </Select>
              </div>
            </FlexBox>
            {filtered.length > 0 && (templateCategory || templateSearch) && (
              <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginTop: '0.5rem' }}>
                {filtered.length} Vorlagen gefunden{templateCategory ? ` in „${categoryLabels[templateCategory]}"` : ''}{templateSearch ? ` für „${templateSearch}"` : ''}
              </Text>
            )}
          </div>
        );
      })()}

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <Label required>Name</Label>
          <HelpTooltip text={fieldHelp.serviceProvider.name} inline />
        </div>
        <Input
          value={formData.name}
          onInput={(e: any) => updateFormData('name', e.target.value)}
          placeholder="z.B. Salesforce, AWS, Google Analytics"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
          <Label required>Rolle</Label>
          <HelpTooltip text={fieldHelp.serviceProvider.role} inline />
        </div>
        <FlexBox direction="Column" style={{ gap: '0.5rem' }}>
          {Object.entries(roleConfig).map(([key, { label, description }]) => {
            const isSelected = formData.role === key;
            return (
              <div
                key={key}
                onClick={() => updateFormData('role', key as ServiceProviderRole)}
                style={{
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: isSelected
                    ? '2px solid var(--sapBrandColor)'
                    : '1px solid var(--sapField_BorderColor)',
                  backgroundColor: isSelected ? 'rgba(0, 112, 242, 0.06)' : 'transparent',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                <RadioButton
                  name="role"
                  text={label}
                  checked={isSelected}
                  onChange={() => updateFormData('role', key as ServiceProviderRole)}
                />
                <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', marginLeft: '1.75rem', display: 'block' }}>
                  {description}
                </Text>
              </div>
            );
          })}
        </FlexBox>
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Adresse</Label>
        <Input
          value={formData.address || ''}
          onInput={(e: any) => updateFormData('address', e.target.value)}
          placeholder="Straße, PLZ Ort, Land"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Kontaktperson</Label>
        <Input
          value={formData.contact_person || ''}
          onInput={(e: any) => updateFormData('contact_person', e.target.value)}
          placeholder="Name der Kontaktperson"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Kontakt-E-Mail</Label>
        <Input
          value={formData.contact_email || ''}
          onInput={(e: any) => updateFormData('contact_email', e.target.value)}
          placeholder="datenschutz@beispiel.de"
          style={{ width: '100%' }}
        />
      </div>

      <div>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Kontakt-Telefon</Label>
        <Input
          value={formData.contact_phone || ''}
          onInput={(e: any) => updateFormData('contact_phone', e.target.value)}
          placeholder="+49 123 456789"
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );

  // Step 2: Leistung & Standort
  const renderStep2 = () => (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Leistungsbeschreibung</Label>
        <TextArea
          value={formData.service_description || ''}
          onInput={(e: any) => updateFormData('service_description', e.target.value)}
          placeholder="Welche Dienstleistung erbringt dieser Anbieter?"
          rows={4}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}>
          <Label>Hosting-Standort</Label>
          <HelpTooltip text={fieldHelp.serviceProvider.hosting_location} inline />
        </div>
        <Select
          onChange={(e: any) => updateFormData('hosting_location', e.detail.selectedOption.value)}
          style={{ width: '100%' }}
        >
          <Option value="">-- Kein Standort angegeben --</Option>
          {hostingLocationOptions.map(opt => (
            <Option
              key={opt.value}
              value={opt.value}
              selected={formData.hosting_location === opt.value}
            >
              {opt.label}
            </Option>
          ))}
        </Select>
      </div>

      <div>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Notizen</Label>
        <TextArea
          value={formData.notes || ''}
          onInput={(e: any) => updateFormData('notes', e.target.value)}
          placeholder="Interne Notizen zu diesem Dienstleister"
          rows={4}
          style={{ width: '100%' }}
        />
      </div>
    </div>
  );

  // Step 3: AVV anlegen
  const renderStep3 = () => (
    <div>
      <MessageStrip design="Information" hideCloseButton style={{ marginBottom: '1.5rem' }}>
        Für Auftragsverarbeiter ist ein AVV gemäß Art. 28 DSGVO erforderlich
      </MessageStrip>

      <div style={{ marginBottom: '1rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>AVV Version</Label>
        <Input
          value={avvData.version}
          onInput={(e: any) => updateAvvData('version', e.target.value)}
          placeholder="1.0"
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Label required style={{ display: 'block', marginBottom: '0.25rem' }}>Vertragsbeginn</Label>
        <DatePicker
          formatPattern="dd.MM.yyyy"
          value={isoToGerman(avvData.start_date)}
          onChange={(e: any) => updateAvvData('start_date', germanToIso(e.detail.value))}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Vertragsende (optional)</Label>
        <DatePicker
          formatPattern="dd.MM.yyyy"
          value={isoToGerman(avvData.end_date)}
          onChange={(e: any) => updateAvvData('end_date', germanToIso(e.detail.value))}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Subunternehmer</Label>
        <Text style={{ fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)', display: 'block', marginBottom: '0.5rem' }}>
          Welche Subunternehmer setzt der Dienstleister ein?
        </Text>
        <FlexBox style={{ gap: '0.5rem' }}>
          <Input
            value={subcontractorInput}
            onInput={(e: any) => { const v = e.target.value ?? ''; setSubcontractorInput(v); subInputRef.current = v; }}
            placeholder="Subunternehmer hinzufügen"
            style={{ flex: 1 }}
            onKeyDown={(e: any) => { if (e.key === 'Enter') { e.preventDefault(); addSubcontractor(); } }}
          />
          <Button icon="add" onClick={addSubcontractor}>Hinzufügen</Button>
        </FlexBox>

        {avvData.subcontractors.length > 0 && (
          <FlexBox direction="Column" style={{ gap: '0.25rem', marginTop: '0.75rem' }}>
            {avvData.subcontractors.map((sub, index) => (
              <FlexBox
                key={index}
                alignItems="Center"
                justifyContent="SpaceBetween"
                style={{
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'var(--sapList_Background, #fff)',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--sapList_BorderColor, #e5e5e5)'
                }}
              >
                <Text>{sub}</Text>
                <Button icon="delete" design="Transparent" onClick={() => removeSubcontractor(index)} tooltip="Entfernen" />
              </FlexBox>
            ))}
          </FlexBox>
        )}
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>TOM-Bewertung</Label>
        <TextArea
          value={avvData.tom_assessment}
          onInput={(e: any) => updateAvvData('tom_assessment', e.target.value)}
          placeholder="Beschreibung der technischen und organisatorischen Maßnahmen"
          rows={4}
          style={{ width: '100%' }}
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <CheckBox
          checked={avvData.tom_adequate}
          onChange={(e: any) => updateAvvData('tom_adequate', e.target.checked)}
          text="TOM ausreichend"
        />
      </div>

      <div style={{ marginBottom: '1.5rem' }}>
        <Label style={{ display: 'block', marginBottom: '0.25rem' }}>Lokaler Pfad (optional)</Label>
        <Input
          value={avvData.local_path}
          onInput={(e: any) => updateAvvData('local_path', e.target.value)}
          placeholder="z.B. C:\Dokumente\AVV\avv-anbieter.pdf"
          style={{ width: '100%' }}
        />
      </div>

      <MessageStrip design="Information" hideCloseButton>
        PDF-Dokumente können nach dem Erstellen im Dienstleister-Detail hochgeladen werden.
      </MessageStrip>
    </div>
  );

  if (initialLoading) {
    return (
      <FlexBox alignItems="Center" justifyContent="Center" style={{ height: '100%' }}>
        <BusyIndicator active size="Large" />
      </FlexBox>
    );
  }

  // Determine which steps to display
  const visibleSteps = editMode
    ? steps.slice(0, 2)
    : requiresAvv(formData.role)
      ? steps
      : steps.slice(0, 2);

  const renderStepContent = () => {
    switch (currentStep) {
      case 0: return renderStep1();
      case 1: return renderStep2();
      case 2: return renderStep3();
      default: return null;
    }
  };

  return (
    <div className="page-layout--narrow">
      <Button
        icon="nav-back"
        design="Transparent"
        onClick={() => navigate('/service-providers')}
        style={{ marginBottom: '0.75rem' }}
      >
        Zurück
      </Button>

      <Title level="H3" style={{ marginBottom: '1rem' }}>
        {editMode ? `Dienstleister bearbeiten: ${formData.name}` : 'Neuen Dienstleister anlegen'}
      </Title>

      {/* Step Indicator */}
      <div style={{ marginBottom: '1.5rem' }}>
        <ProgressIndicator
          value={Math.round(((currentStep + 1) / visibleSteps.length) * 100)}
          displayValue={`Schritt ${currentStep + 1} von ${visibleSteps.length}`}
        />
        <FlexBox justifyContent="SpaceBetween" style={{ marginTop: '0.5rem' }}>
          {visibleSteps.map((step, i) => (
            <FlexBox key={i} alignItems="Center" style={{ gap: '0.25rem' }}>
              <Icon name={step.icon} style={{
                fontSize: '0.875rem',
                color: i === currentStep ? 'var(--sapBrandColor)' :
                       i < currentStep ? 'var(--sapPositiveColor)' : 'var(--sapNeutralColor)'
              }} />
              <Text style={{
                fontSize: '0.75rem',
                fontWeight: i === currentStep ? '600' : '400',
                color: i === currentStep ? 'var(--sapBrandColor)' :
                       i < currentStep ? 'var(--sapPositiveColor)' : 'var(--sapNeutralTextColor)'
              }}>
                {step.title}
              </Text>
            </FlexBox>
          ))}
        </FlexBox>
      </div>

      {/* Error */}
      {error && (
        <MessageStrip design="Negative" hideCloseButton style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {/* Step Content */}
      <Card style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '1.5rem' }}>
          {renderStepContent()}
        </div>
      </Card>

      {/* Navigation */}
      <FlexBox justifyContent="SpaceBetween">
        <Button onClick={() => navigate('/service-providers')} design="Transparent">Abbrechen</Button>
        <FlexBox style={{ gap: '0.5rem' }}>
          {currentStep > 0 && (
            <Button onClick={handleBack} icon="nav-back">Zurück</Button>
          )}
          {currentStep < lastStep ? (
            <Button onClick={handleNext} design="Emphasized" icon="feeder-arrow">
              Weiter
            </Button>
          ) : (
            <Button onClick={handleSubmit} design="Emphasized" icon="save" disabled={loading}>
              {loading ? 'Wird gespeichert...' : (editMode ? 'Änderungen speichern' : 'Speichern')}
            </Button>
          )}
        </FlexBox>
      </FlexBox>
    </div>
  );
}
